
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Hotel, UtensilsCrossed } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSearch } from '../context/SearchContext';
import { getUniqueCostValues, DEFAULT_COST_RANGES } from '../utils/costRangeUtils';
import { isValueInActualRange, getNearestValue } from '../utils/filterUtils';

interface PriceRangeFilterProps {
  value: [number, number];
  onChange: (value: [number, number]) => void;
  costType?: string;
  onCostTypeChange?: (type: string) => void;
}

type CostType = 'avg_accommodation_cost' | 'avg_meal_cost';

const COST_TYPES = [
  { 
    value: 'avg_accommodation_cost', 
    label: 'Accommodation', 
    icon: <Hotel className="h-4 w-4 mr-2 text-blue-600" />,
    color: 'bg-blue-50 border-blue-200 text-blue-700',
    histogramColor: '#3B82F6', // Royal blue
    defaultMaxValue: 200,
    precision: 0
  },
  { 
    value: 'avg_meal_cost', 
    label: 'Meal Cost', 
    icon: <UtensilsCrossed className="h-4 w-4 mr-2 text-orange-600" />,
    color: 'bg-orange-50 border-orange-200 text-orange-700',
    histogramColor: '#F59E0B', // Amber
    defaultMaxValue: 100,
    precision: 1
  }
];

// Update the default max ranges
const DEFAULT_MAX_RANGES: Record<CostType, number> = {
  avg_accommodation_cost: 200,
  avg_meal_cost: 100
};

const DEFAULT_HISTOGRAMS: Record<CostType, number[]> = {
  avg_accommodation_cost: [12, 25, 35, 50, 65, 75, 80, 70, 55, 45, 35, 30, 25, 20, 15, 10, 8, 6, 4, 2],
  avg_meal_cost: [15, 30, 45, 60, 75, 85, 70, 55, 45, 35, 30, 25, 20, 15, 10, 7, 5, 3, 2, 1]
};

const HISTOGRAM_BINS = 10;

const normalizeHistogramData = (data: number[]): number[] => {
  try {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return Array(HISTOGRAM_BINS).fill(1);
    }

    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min;
    
    if (range === 0) return data.map(() => 1);
    
    return data.map(value => 0.2 + ((value - min) / range) * 0.8);
  } catch (error) {
    console.error("Error normalizing histogram data:", error);
    return Array(HISTOGRAM_BINS).fill(1);
  }
};

const validateHistogramData = (data: number[]): boolean => {
  if (!data || !Array.isArray(data)) return false;
  if (data.length === 0) return false;
  if (data.some(value => isNaN(value))) return false;
  return true;
};

export const PriceRangeFilter = ({ 
  value, 
  onChange, 
  costType: externalCostType = 'avg_accommodation_cost', 
  onCostTypeChange 
}: PriceRangeFilterProps) => {
  const searchContext = useSearch();
  const { costRanges, region, destinations } = searchContext || { costRanges: {}, region: null, destinations: [] };
  
  const safeValue: [number, number] = [
    typeof value[0] === 'number' && !isNaN(value[0]) ? value[0] : 0,
    typeof value[1] === 'number' && !isNaN(value[1]) ? value[1] : 200
  ];
  
  const validCostType = (externalCostType as CostType === 'avg_accommodation_cost' || 
                    externalCostType as CostType === 'avg_meal_cost') 
                    ? externalCostType as CostType 
                    : 'avg_accommodation_cost';
  
  const [costType, setCostType] = useState<CostType>(validCostType);
  const [localRange, setLocalRange] = useState<[number, number]>(safeValue);
  const [isDirty, setIsDirty] = useState(false);
  const [isHistogramVisible, setIsHistogramVisible] = useState(true);
  const prevExternalCostTypeRef = useRef(externalCostType);
  const prevDestinationsRef = useRef<any[]>([]);
  const [uniqueCostValues, setUniqueCostValues] = useState<number[]>([]);
  
  const defaultHistogram = DEFAULT_HISTOGRAMS[costType] || DEFAULT_HISTOGRAMS.avg_accommodation_cost;
  const [histogram, setHistogram] = useState<number[]>(defaultHistogram);
  
  function generateDefaultHistogram(type: CostType): number[] {
    try {
      const defaultData = DEFAULT_HISTOGRAMS[type] || Array(HISTOGRAM_BINS * 2).fill(10);
      
      if (defaultData.length === HISTOGRAM_BINS) return defaultData;
      
      const result = Array(HISTOGRAM_BINS).fill(0);
      const ratio = defaultData.length / HISTOGRAM_BINS;
      
      for (let i = 0; i < HISTOGRAM_BINS; i++) {
        const startIndex = Math.floor(i * ratio);
        const endIndex = Math.floor((i + 1) * ratio);
        let sum = 0;
        let count = 0;
        
        for (let j = startIndex; j < endIndex; j++) {
          if (defaultData[j] !== undefined) {
            sum += defaultData[j];
            count++;
          }
        }
        
        result[i] = count > 0 ? sum / count : 0;
      }
      
      return result;
    } catch (error) {
      console.error("Error generating default histogram:", error);
      return Array(HISTOGRAM_BINS).fill(1);
    }
  }
  
  useEffect(() => {
    try {
      const destinationsChanged = !areDestinationsEqual(
        Array.isArray(destinations) ? destinations : [],
        Array.isArray(prevDestinationsRef.current) ? prevDestinationsRef.current : []
      );
      
      if ((destinations && Array.isArray(destinations) && destinations.length > 0) && 
          (destinationsChanged || costType !== prevExternalCostTypeRef.current)) {
        console.log("Generating histogram based on fresh data");
        
        const values = getUniqueCostValues(destinations, costType);
        setUniqueCostValues(values);
        
        generateHistogram();
        prevDestinationsRef.current = [...destinations];
      } else {
        console.log("No destinations available or unchanged, using default histogram");
        const defaultHist = generateDefaultHistogram(costType);
        setHistogram(defaultHist);
        setIsHistogramVisible(true);
      }
    } catch (error) {
      console.error("Error in PriceRangeFilter useEffect:", error);
      setHistogram(generateDefaultHistogram(costType));
    }
  }, [destinations, costType]);

  const areDestinationsEqual = (a: any[], b: any[]): boolean => {
    try {
      if (!Array.isArray(a) || !Array.isArray(b)) return false;
      if (a.length !== b.length) return false;
      if (a.length === 0 && b.length === 0) return true;
      
      const aIds = new Set(a.map(dest => dest?.id).filter(Boolean));
      return b.every(dest => dest?.id && aIds.has(dest.id));
    } catch (error) {
      console.error("Error comparing destinations:", error);
      return false;
    }
  };

  const generateHistogram = () => {
    try {
      if (!destinations || !Array.isArray(destinations) || destinations.length === 0) {
        console.log("No destinations available for histogram generation, using defaults");
        const defaultData = generateDefaultHistogram(costType);
        setHistogram(defaultData);
        return;
      }

      const values: number[] = destinations
        .map(dest => {
          if (!dest || !dest.costs_infrastructure) return NaN;
          const cost = dest.costs_infrastructure[costType];
          return typeof cost === 'number' ? cost : parseFloat(String(cost));
        })
        .filter(cost => !isNaN(cost) && cost >= 0);

      if (values.length === 0) {
        console.log("No valid cost values found, using default histogram");
        const defaultData = generateDefaultHistogram(costType);
        setHistogram(defaultData);
        return;
      }

      const min = Math.min(...values);
      const max = Math.max(...values);
      const range = max - min;
      
      if (range === 0) {
        setHistogram(Array(HISTOGRAM_BINS).fill(1));
        return;
      }
      
      const binSize = range / HISTOGRAM_BINS;
      const bins = Array(HISTOGRAM_BINS).fill(0);
      
      values.forEach(value => {
        let binIndex = Math.floor((value - min) / binSize);
        if (binIndex === HISTOGRAM_BINS) binIndex--;
        bins[binIndex]++;
      });

      const smoothedBins = smoothHistogram(bins);
      const normalizedBins = normalizeHistogramData(smoothedBins);
      
      console.log('Generated histogram:', {
        costType,
        min,
        max,
        binSize,
        originalBins: bins,
        smoothedBins,
        normalizedBins,
        valuesCount: values.length
      });

      setHistogram(normalizedBins);
    } catch (error) {
      console.error("Error generating histogram:", error);
      setHistogram(generateDefaultHistogram(costType));
    }
  };

  const smoothHistogram = (bins: number[]): number[] => {
    try {
      if (!Array.isArray(bins) || bins.length <= 3) return bins || [];
      
      const smoothed = [...bins];
      const iterations = bins.length < 5 ? 1 : 2;
      
      for (let iteration = 0; iteration < iterations; iteration++) {
        const temp = [...smoothed];
        
        for (let i = 0; i < bins.length; i++) {
          let sum = 0;
          let weights = 0;
          
          for (let j = -2; j <= 2; j++) {
            const index = i + j;
            if (index >= 0 && index < bins.length) {
              const weight = Math.exp(-(j * j) / 4);
              sum += temp[index] * weight;
              weights += weight;
            }
          }
          
          smoothed[i] = sum / weights;
        }
      }
      
      return smoothed;
    } catch (error) {
      console.error("Error smoothing histogram:", error);
      return bins || Array(HISTOGRAM_BINS).fill(1);
    }
  };

  useEffect(() => {
    if (externalCostType !== prevExternalCostTypeRef.current) {
      console.log('Cost type changed from', prevExternalCostTypeRef.current, 'to', externalCostType);
      
      const newType = (externalCostType === 'avg_accommodation_cost' || 
                        externalCostType === 'avg_meal_cost') 
                      ? externalCostType as CostType 
                      : 'avg_accommodation_cost';
      
      setCostType(newType);
      prevExternalCostTypeRef.current = externalCostType;
      
      setIsDirty(false);
    }
  }, [externalCostType]);
  
  useEffect(() => {
    if (!isDirty) {
      const newValue: [number, number] = [
        typeof value[0] === 'number' && !isNaN(value[0]) ? value[0] : 0,
        typeof value[1] === 'number' && !isNaN(value[1]) ? value[1] : 200
      ];
      
      console.log('Updating from props:', value, 'to', newValue);
      setLocalRange(newValue);
    }
  }, [value, isDirty]);

  const handleCostTypeChange = (newType: CostType) => {
    try {
      console.log('Cost type change requested to:', newType);
      setCostType(newType);
      
      let newRange: [number, number];
      
      if (costRanges && costRanges[newType]) {
        newRange = costRanges[newType];
        console.log('Using range from context for new cost type:', newRange);
      } else {
        newRange = [0, DEFAULT_MAX_RANGES[newType]];
        console.log('Using default range for new cost type:', newRange);
      }
      
      console.log('Setting new range for cost type change:', newRange);
      setLocalRange(newRange);
      setIsDirty(true);
      
      setHistogram(generateDefaultHistogram(newType));
      
      if (onCostTypeChange) {
        onCostTypeChange(newType);
      }
    } catch (error) {
      console.error("Error handling cost type change:", error);
      // Fallback to default values if something goes wrong
      const defaultType = 'avg_accommodation_cost' as CostType;
      setCostType(defaultType);
      setLocalRange([0, DEFAULT_MAX_RANGES[defaultType]]);
      setHistogram(generateDefaultHistogram(defaultType));
      
      if (onCostTypeChange) {
        onCostTypeChange(defaultType);
      }
    }
  };

  const handleRangeChange = (newRange: number[]) => {
    try {
      if (Array.isArray(newRange) && newRange.length === 2) {
        const typedRange: [number, number] = [
          parseFloat(String(newRange[0])) || 0, 
          parseFloat(String(newRange[1])) || DEFAULT_MAX_RANGES[costType]
        ];
        console.log('Range change:', newRange, 'to', typedRange);
        setLocalRange(typedRange);
        setIsDirty(true);
      }
    } catch (error) {
      console.error("Error handling range change:", error);
    }
  };

  const handleMinInputChange = (value: string) => {
    try {
      const num = parseFloat(value);
      
      if (!isNaN(num)) {
        const validMin = Math.max(0, Math.min(num, localRange[1] - getStep()));
        handleRangeChange([validMin, localRange[1]]);
      }
    } catch (error) {
      console.error("Error handling min input change:", error);
    }
  };

  const handleMaxInputChange = (value: string) => {
    try {
      const num = parseFloat(value);
      
      const maxValue = getMaxValue();
      
      if (!isNaN(num)) {
        const validMax = Math.min(maxValue, Math.max(num, localRange[0] + getStep()));
        handleRangeChange([localRange[0], validMax]);
      }
    } catch (error) {
      console.error("Error handling max input change:", error);
    }
  };

  const getStep = () => costType === 'avg_meal_cost' ? 0.1 : 1;

  const getMaxValue = () => {
    try {
      if (costRanges && costRanges[costType] && 
          Array.isArray(costRanges[costType]) && 
          costRanges[costType].length >= 2 &&
          !isNaN(costRanges[costType][1])) {
        // Always limit the max value for accommodation cost to 200
        if (costType === 'avg_accommodation_cost') {
          const contextMax = Math.min(costRanges[costType][1], 200);
          console.log(`Using capped max value for ${costType}:`, contextMax);
          return contextMax;
        } else {
          const contextMax = costRanges[costType][1];
          console.log(`Using max value from context for ${costType}:`, contextMax);
          return contextMax;
        }
      }
    } catch (e) {
      console.error("Error getting max value:", e);
    }
    
    console.log(`Using default max value for ${costType}:`, DEFAULT_MAX_RANGES[costType]);
    return DEFAULT_MAX_RANGES[costType];
  };

  const getMinValue = () => {
    try {
      if (costRanges && costRanges[costType] && 
          Array.isArray(costRanges[costType]) && 
          costRanges[costType].length >= 1 &&
          !isNaN(costRanges[costType][0])) {
        const contextMin = costRanges[costType][0];
        console.log(`Using min value from context for ${costType}:`, contextMin);
        return contextMin;
      }
    } catch (e) {
      console.error("Error getting min value:", e);
    }
    
    console.log(`Using default min value for ${costType}:`, 0);
    return 0;
  };

  const handleApplyFilter = () => {
    try {
      console.log('Applying filter, sending to parent:', localRange);
      
      // Fixed: Don't automatically adjust the range to nearest values
      let adjustedRange: [number, number] = [...localRange] as [number, number];
      
      // Only adjust if values are outside the absolute bounds
      const maxValue = getMaxValue();
      const minValue = getMinValue();
      
      if (adjustedRange[0] < minValue) adjustedRange[0] = minValue;
      if (adjustedRange[1] > maxValue) adjustedRange[1] = maxValue;
      
      // Log all cost values for debugging
      console.log('Available cost values:', uniqueCostValues);
      console.log('User selected range:', localRange);
      console.log('Adjusted range (only if outside bounds):', adjustedRange);
      
      onChange([
        parseFloat(String(adjustedRange[0])) || 0,
        parseFloat(String(adjustedRange[1])) || DEFAULT_MAX_RANGES[costType]
      ]);
      setIsDirty(false);
    } catch (error) {
      console.error("Error applying filter:", error);
    }
  };

  const selectedType = COST_TYPES.find(type => type.value === costType) || COST_TYPES[0];
  
  const maxValue = getMaxValue();
  const minValue = getMinValue();
  
  const binWidth = (maxValue - minValue) / HISTOGRAM_BINS;
  
  const getPrecision = () => {
    const typeConfig = COST_TYPES.find(type => type.value === costType);
    return typeConfig?.precision || 0;
  };

  const formatPrice = (price: number) => {
    return (price || 0).toFixed(getPrecision());
  };

  const renderHistogram = () => {
    try {
      if (!validateHistogramData(histogram)) {
        console.log("Invalid histogram data, regenerating");
        const defaultData = generateDefaultHistogram(costType);
        setHistogram(defaultData);
        return null;
      }

      const maxHeight = Math.max(...histogram, 0.1);
      const safeMinBinIndex = Math.max(0, Math.min(
        Math.floor((localRange[0] - minValue) / ((maxValue - minValue) / HISTOGRAM_BINS)), 
        HISTOGRAM_BINS - 1
      ));
      const safeMaxBinIndex = Math.max(0, Math.min(
        Math.ceil((localRange[1] - minValue) / ((maxValue - minValue) / HISTOGRAM_BINS)),
        HISTOGRAM_BINS - 1
      ));

      return histogram.map((height, index) => {
        const isCovered = index >= safeMinBinIndex && index <= safeMaxBinIndex;
        const heightPercent = (height / maxHeight) * 100;
        
        return (
          <div 
            key={index}
            className="rounded-t-md transition-all duration-300 group relative"
            style={{ 
              height: `${Math.max(heightPercent, 5)}%`,
              flexGrow: 1,
              background: isCovered 
                ? selectedType.histogramColor || '#8B5CF6' 
                : '#E5E7EB',
              opacity: isCovered ? 0.9 : 0.4
            }}
          >
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-800 text-white text-xs px-2 py-1 rounded pointer-events-none z-10">
              {((height || 0) * maxHeight).toFixed(1)}
            </div>
          </div>
        );
      });
    } catch (error) {
      console.error("Error rendering histogram:", error);
      return null;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Label className="text-sm font-medium mb-3 block text-gray-700">
          Cost Type <span className="text-xs text-gray-500">${formatPrice(minValue)}-${formatPrice(maxValue)}</span>
        </Label>
        <div className="grid grid-cols-2 gap-2">
          {COST_TYPES.map((type) => (
            <button
              key={type.value}
              type="button"
              className={`flex items-center justify-center px-3 py-2 rounded-md border transition-all ${
                costType === type.value 
                  ? type.color + ' font-medium shadow-sm' 
                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
              }`}
              onClick={() => handleCostTypeChange(type.value as CostType)}
            >
              {type.icon}
              <span className="text-sm font-medium">{type.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium text-gray-700">
            Your budget
          </div>
          <div className="text-sm font-medium text-gray-800">
            ${formatPrice(localRange[0])} - ${formatPrice(localRange[1])}
          </div>
        </div>

        <div className="relative h-28 mt-8 mb-10">
          <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
            <div className="border-t border-gray-100"></div>
            <div className="border-t border-gray-100"></div>
            <div className="border-t border-gray-100"></div>
          </div>
          
          <div className="absolute bottom-0 left-0 right-0 flex items-end h-20 gap-[3px]">
            {renderHistogram()}
          </div>

          <div className="absolute left-0 right-0 bottom-[-20px]">
            <Slider
              value={localRange}
              min={minValue}
              max={maxValue}
              step={getStep()}
              onValueChange={handleRangeChange}
              className="z-10"
            />
          </div>

          <div className="absolute left-0 right-0 bottom-[-40px] flex justify-between">
            <div className="flex flex-col items-center">
              <span className="text-xs font-medium text-gray-700">${formatPrice(localRange[0])}</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-xs font-medium text-gray-700">${formatPrice(localRange[1])}</span>
            </div>
          </div>
        </div>

        <div className="mt-20 pt-2">
          <div className="flex items-center justify-between gap-4">
            <div className="w-1/2">
              <Input
                type="number"
                className="h-9 px-3 py-1 text-sm font-medium"
                value={localRange[0]}
                min={minValue}
                max={localRange[1] - getStep()}
                step={getStep()}
                onChange={(e) => handleMinInputChange(e.target.value)}
              />
            </div>
            <span className="text-gray-500">-</span>
            <div className="w-1/2">
              <Input
                type="number"
                className="h-9 px-3 py-1 text-sm font-medium"
                value={localRange[1]}
                min={localRange[0] + getStep()}
                max={maxValue}
                step={getStep()}
                onChange={(e) => handleMaxInputChange(e.target.value)}
              />
            </div>
          </div>
          
          {isDirty && (
            <div className="flex justify-end mt-4">
              <Button 
                size="sm"
                variant="default"
                onClick={handleApplyFilter}
              >
                Apply Filter
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
